// Convention:
// - Everything lowercase is an object
// - Everything uppercase is a string (the route)

import { keys } from '../keys';

export const baseUrl = {
  Dashboard: keys().NEXT_PUBLIC_DASHBOARD_URL,
  Marketing: keys().NEXT_PUBLIC_MARKETING_URL,
  PublicApi: keys().NEXT_PUBLIC_PUBLIC_API_URL
  // App: 'https://app.videomule.ai'
} as const;

export const routes = {
  dashboard: {
    Api: `${baseUrl.Dashboard}/api`,
    appsumo: {
      Activate: `${baseUrl.Dashboard}/appsumo/activate`
    },
    auth: {
      changeEmail: {
        Expired: `${baseUrl.Dashboard}/auth/change-email/expired`,
        Index: `${baseUrl.Dashboard}/auth/change-email`,
        Invalid: `${baseUrl.Dashboard}/auth/change-email/invalid`,
        Request: `${baseUrl.Dashboard}/auth/change-email/request`
      },
      Error: `${baseUrl.Dashboard}/auth/error`,
      forgetPassword: {
        Index: `${baseUrl.Dashboard}/auth/forgot-password`,
        Success: `${baseUrl.Dashboard}/auth/forgot-password/success`
      },
      Index: `${baseUrl.Dashboard}/auth`,
      RecoveryCode: `${baseUrl.Dashboard}/auth/recovery-code`,
      resetPassword: {
        Expired: `${baseUrl.Dashboard}/auth/reset-password/expired`,
        Index: `${baseUrl.Dashboard}/auth/reset-password`,
        Request: `${baseUrl.Dashboard}/auth/reset-password/request`,
        Success: `${baseUrl.Dashboard}/auth/reset-password/success`
      },
      restoreAccount: {
        Expired: `${baseUrl.Dashboard}/auth/restore-account/expired`,
        Index: `${baseUrl.Dashboard}/auth/restore-account`
      },
      SignIn: `${baseUrl.Dashboard}/auth/sign-in`,
      SignUp: `${baseUrl.Dashboard}/auth/sign-up`,
      Totp: `${baseUrl.Dashboard}/auth/totp`,
      verifyEmail: {
        Expired: `${baseUrl.Dashboard}/auth/verify-email/expired`,
        Index: `${baseUrl.Dashboard}/auth/verify-email`,
        Request: `${baseUrl.Dashboard}/auth/verify-email/request`,
        Success: `${baseUrl.Dashboard}/auth/verify-email/success`
      }
    },
    Index: `${baseUrl.Dashboard}/`,
    invitations: {
      AlreadyAccepted: `${baseUrl.Dashboard}/invitations/already-accepted`,
      Index: `${baseUrl.Dashboard}/invitations`,
      Request: `${baseUrl.Dashboard}/invitations/request`,
      Revoked: `${baseUrl.Dashboard}/invitations/revoked`
    },
    onboarding: {
      Index: `${baseUrl.Dashboard}/onboarding`,
      Organization: `${baseUrl.Dashboard}/onboarding/organization`,
      User: `${baseUrl.Dashboard}/onboarding/user`
    },
    organizations: {
      Index: `${baseUrl.Dashboard}/org`,
      slug: {
        ChoosePlan: `${baseUrl.Dashboard}/org/[slug]/choose-plan`,
        Contacts: `${baseUrl.Dashboard}/org/[slug]/contacts`,
        Index: `${baseUrl.Dashboard}/org/[slug]`,
        projects: {
          Index: `${baseUrl.Dashboard}/org/[slug]/projects`,
          New: `${baseUrl.Dashboard}/org/[slug]/projects/new`,
          id: {
            Script: `${baseUrl.Dashboard}/org/[slug]/projects/[id]/script`,
            OutlineWaiting: `${baseUrl.Dashboard}/org/[slug]/projects/[id]/outline-waiting`,
            Preview: `${baseUrl.Dashboard}/org/[slug]/projects/[id]/preview`,
            Editor: `${baseUrl.Dashboard}/org/[slug]/projects/[id]/editor`
          }
        },
        Usage: `${baseUrl.Dashboard}/org/[slug]/usage`,
        Exports: `${baseUrl.Dashboard}/org/[slug]/exports`,
        MediaLibrary: `${baseUrl.Dashboard}/org/[slug]/media-library`,
        VoiceLibrary: `${baseUrl.Dashboard}/org/[slug]/voice-library`,
        AvatarLibrary: `${baseUrl.Dashboard}/org/[slug]/avatar-library`,
        AvatarLibraryCharacter: `${baseUrl.Dashboard}/org/[slug]/avatar-library/[characterKey]`,
        FeatureRequests: `${baseUrl.Dashboard}/org/[slug]/feature-requests`,
        settings: {
          account: {
            Index: `${baseUrl.Dashboard}/org/[slug]/settings/account`,
            Notifications: `${baseUrl.Dashboard}/org/[slug]/settings/account/notifications`,
            Profile: `${baseUrl.Dashboard}/org/[slug]/settings/account/profile`,
            Security: `${baseUrl.Dashboard}/org/[slug]/settings/account/security`
          },
          Index: `${baseUrl.Dashboard}/org/[slug]/settings`,
          organization: {
            Billing: `${baseUrl.Dashboard}/org/[slug]/settings/organization/billing`,
            Developers: `${baseUrl.Dashboard}/org/[slug]/settings/organization/developers`,
            General: `${baseUrl.Dashboard}/org/[slug]/settings/organization/general`,
            Index: `${baseUrl.Dashboard}/org/[slug]/settings/organization`,
            Members: `${baseUrl.Dashboard}/org/[slug]/settings/organization/members`
          }
        }
      }
    }
  },
  marketing: {
    About: `${baseUrl.Marketing}/about`,
    Api: `${baseUrl.Marketing}/api`,
    Blog: `${baseUrl.Marketing}/blog`,
    Careers: `${baseUrl.Marketing}/careers`,
    Contact: `${baseUrl.Marketing}/contact`,
    CookiePolicy: `${baseUrl.Marketing}/cookie-policy`,
    Docs: `${baseUrl.Marketing}/docs`,
    tools: {
      AiProductVideoMaker: `${baseUrl.Marketing}/tools/ai-product-video-maker`,
      ProductDemoVideoMaker: `${baseUrl.Marketing}/tools/product-demo-video-maker`,
      AiVideoScriptGenerator: `${baseUrl.Marketing}/tools/ai-video-script-generator`,
      AiTutorialVideoMaker: `${baseUrl.Marketing}/tools/ai-tutorial-video-maker`,
      AiEducationalVideoMaker: `${baseUrl.Marketing}/tools/ai-educational-video-maker`,
      AiScreenRecorder: `${baseUrl.Marketing}/tools/ai-screen-recorder`,
      AiVoiceoverGenerator: `${baseUrl.Marketing}/tools/ai-voiceover-generator`,
      AiTrainingVideos: `${baseUrl.Marketing}/tools/ai-training-videos`,
      AiAvatarVideoMaker: `${baseUrl.Marketing}/tools/ai-avatar-video-maker`,
      AiVideoClone: `${baseUrl.Marketing}/tools/ai-video-clone`,
      AiVideoTranslator: `${baseUrl.Marketing}/tools/ai-video-translator`,
      AiVoiceCloning: `${baseUrl.Marketing}/tools/ai-voice-cloning`
    },
    Changelog: `${baseUrl.Marketing}/changelog`,
    Index: `${baseUrl.Marketing}/`,
    Pricing: `${baseUrl.Marketing}/pricing`,
    PrivacyPolicy: `${baseUrl.Marketing}/privacy-policy`,
    Security: `${baseUrl.Marketing}/security`,
    // Roadmap: 'https://achromatic.canny.io',
    AffiliateProgram: `${baseUrl.Marketing}/affiliate-program`,
    TermsOfUse: `${baseUrl.Marketing}/terms-of-use`
  }
} as const;

type ExtractSlugRoutes<T> =
  T extends Record<string, unknown>
    ? {
        [K in keyof T]: T[K] extends string
          ? T[K] extends `${string}[slug]${string}`
            ? T[K]
            : never
          : ExtractSlugRoutes<T[K]>;
      }[keyof T]
    : never;

type OrganizationsSlugRoutes = ExtractSlugRoutes<
  typeof routes.dashboard.organizations.slug
>;

export function replaceOrgSlug(
  route: OrganizationsSlugRoutes,
  slug: string
): string {
  if (route.indexOf('[slug]') === -1) {
    throw new Error(
      `Invalid route: ${route}. Route must contain the placeholder [slug].`
    );
  }

  return route.replace('[slug]', slug);
}

export function replaceProjectParams(
  route: string,
  slug: string,
  projectId: string
): string {
  if (route.indexOf('[slug]') === -1 || route.indexOf('[id]') === -1) {
    throw new Error(
      `Invalid route: ${route}. Route must contain both [slug] and [id] placeholders.`
    );
  }

  return route.replace('[slug]', slug).replace('[id]', projectId);
}

export function getPathname(route: string, baseUrl: string): string {
  return new URL(route, baseUrl).pathname;
}

export function getOrganizationLogoUrl(
  organizationId: string,
  hash: string
): string {
  return `${routes.dashboard.Api}/organization-logos/${organizationId}?v=${hash}`;
}

export function getUserImageUrl(userId: string, hash: string): string {
  return `${routes.dashboard.Api}/user-images/${userId}?v=${hash}`;
}

export function getContactImageUrl(contactId: string, hash: string): string {
  return `${routes.dashboard.Api}/contact-images/${contactId}?v=${hash}`;
}
